import axios from "axios"

const state = {
  list: [],
  count: 0,
  next: null,
  previous: null,
  stats: null,
  preferences: null,
  loading: false,
  error: null,
  showPanel: false,
}

const getters = {
  notificationsList: state => state.list,
  notificationsCount: state => state.count,
  notificationsNext: state => state.next,
  notificationsPrevious: state => state.previous,
  notificationsStats: state => state.stats,
  notificationsPreferences: state => state.preferences,
  notificationsLoading: state => state.loading,
  notificationsError: state => state.error,
  notificationsShowPanel: state => state.showPanel,
  notificationsUnreadCount: state =>
    // state.list.filter(n => !n.is_read).length ||
    state.stats?.unread_notifications || 0,
}

const mutations = {
  SET_LIST(state, payload) {
    state.list = payload.results || []
    state.count = payload.count || 0
    state.next = payload.next || null
    state.previous = payload.previous || null
  },
  SET_STATS(state, stats) {
    state.stats = stats || null
  },
  SET_PREFERENCES(state, prefs) {
    state.preferences = prefs || null
  },
  SET_LOADING(state, val) {
    state.loading = !!val
  },
  SET_ERROR(state, err) {
    state.error = err || null
  },
  TOGGLE_PANEL(state, val) {
    state.showPanel = typeof val === "boolean" ? val : !state.showPanel
  },
  MARK_READ_LOCALLY(state, ids) {
    const setIds = new Set(Array.isArray(ids) ? ids : [ids])
    state.list = state.list.map(n =>
      setIds.has(n.id) ? { ...n, is_read: true } : n
    )
    if (state.stats && state.stats.unread_notifications > 0) {
      const dec = Array.isArray(ids) ? ids.length : 1
      state.stats.unread_notifications = Math.max(
        0,
        state.stats.unread_notifications - dec
      )
    }
  },
}

const actions = {
  togglePanel({ commit }, value) {
    commit("TOGGLE_PANEL", value)
  },
  async fetchList({ commit }, { params = {}, headers, config } = {}) {
    commit("SET_LOADING", true)
    commit("SET_ERROR", null)
    try {
      const res = await axios.get(
        `${process.env.VUE_APP_APIEndPoint}notifications/list`,
        { params, ...headers }
      )
      let data = res.data?.data || { results: [], count: 0 }

      // Add dummy notifications for testing if list is empty
      if (!data.results || data.results.length === 0) {
        const totalDummyNotifications = 200
        const page = parseInt(params.page) || 1
        const limit = parseInt(params.limit) || 20

        // Generate all dummy notifications
        const allDummyNotifications = Array.from(
          { length: totalDummyNotifications },
          (_, index) => {
            const types = [
              "homework_assigned",
              "homework_graded",
              "feedback_provided",
              "due_reminder",
            ]
            const subjects = ["Mathematics", "Arabic", "Quran", "Tajweed"]
            const priorities = ["urgent", "high", "medium", "low"]
            const icons = ["📚", "📝", "💬", "⏰"]
            const type = types[index % 4]
            const subject = subjects[index % 4]

            let title, message

            switch (type) {
              case "homework_assigned":
                title = `📚 New Homework: ${subject} Assignment`
                message = `A new homework assignment has been assigned in ${subject}. Due: ${new Date(Date.now() + (index + 1) * 24 * 60 * 60 * 1000).toLocaleDateString()}`
                break
              case "homework_graded":
                title = `📝 Homework Graded: ${subject}`
                message = `Your ${subject} homework has been graded. Check your results and feedback.`
                break
              case "feedback_provided":
                title = `💬 New Feedback: ${subject}`
                message = `Your tutor has provided detailed feedback on your ${subject} assignment. Review it to improve your work.`
                break
              case "due_reminder":
                title = `⏰ Due Reminder: ${subject} Homework`
                message = `Reminder: Your ${subject} homework is due tomorrow. Please submit it on time.`
                break
              default:
                title = `📚 Notification ${index + 1}`
                message = `You have a new notification regarding ${subject}.`
            }

            return {
              id: index + 1,
              notification_type: type,
              title: title,
              message: message,
              priority: priorities[index % 4],
              is_read: index % 2 === 0,
              is_urgent: index === 0,
              notification_icon: icons[index % 4],
              data: {
                homework_id: 100 + index,
                subject: subject,
                due_date: new Date(
                  Date.now() + (index + 1) * 24 * 60 * 60 * 1000
                )
                  .toISOString()
                  .split("T")[0],
              },
              content_object: {
                id: 100 + index,
                title: `${subject} Assignment ${index + 1}`,
              },
              created_on: new Date(
                Date.now() - index * 60 * 60 * 1000
              ).toISOString(),
            }
          }
        )

        // Apply pagination to dummy data
        const startIndex = (page - 1) * limit
        const endIndex = startIndex + limit
        const paginatedResults = allDummyNotifications.slice(
          startIndex,
          endIndex
        )

        // Calculate pagination URLs
        const totalPages = Math.ceil(totalDummyNotifications / limit)
        const baseUrl = `${process.env.VUE_APP_APIEndPoint}notifications/list`

        let nextUrl = null
        let previousUrl = null

        if (page < totalPages) {
          const nextParams = new URLSearchParams({ ...params, page: page + 1 })
          nextUrl = `${baseUrl}?${nextParams.toString()}`
        }

        if (page > 1) {
          const prevParams = new URLSearchParams({ ...params, page: page - 1 })
          previousUrl = `${baseUrl}?${prevParams.toString()}`
        }

        data = {
          results: paginatedResults,
          count: totalDummyNotifications,
          next: nextUrl,
          previous: previousUrl,
        }
      }

      commit("SET_LIST", data)
      return data
    } catch (err) {
      commit("SET_ERROR", err)
      throw err
    } finally {
      commit("SET_LOADING", false)
    }
  },
  async fetchStats({ commit }, { headers, config } = {}) {
    try {
      const res = await axios.get(
        `${process.env.VUE_APP_APIEndPoint}notifications/stats/`,
        headers
      )
      commit("SET_STATS", res.data?.data || null)
    } catch (err) {
      commit("SET_ERROR", err)
      throw err
    }
  },
  async fetchPreferences({ commit }, { headers, config } = {}) {
    try {
      const res = await axios.get(
        `${process.env.VUE_APP_APIEndPoint}notifications/preferences/`,
        headers
      )
      commit("SET_PREFERENCES", res.data?.data || null)
    } catch (err) {
      commit("SET_ERROR", err)
      throw err
    }
  },
  async updatePreferences({ commit }, { payload, headers, config }) {
    const res = await axios.put(
      `${process.env.VUE_APP_APIEndPoint}notifications/preferences/`,
      payload,
      headers
    )
    commit("SET_PREFERENCES", res.data?.data || null)
    return res.data?.data
  },
  async markRead({ commit }, { ids, headers, config }) {
    const payload = { ids: Array.isArray(ids) ? ids : [ids] }
    await axios.post(
      `${process.env.VUE_APP_APIEndPoint}notifications/mark-read/`,
      payload,
      headers
    )
    commit("MARK_READ_LOCALLY", payload.ids)
  },
  async archive({ dispatch }, { ids, headers, config, params = {} }) {
    const payload = { ids: Array.isArray(ids) ? ids : [ids] }
    await axios.post(
      `${process.env.VUE_APP_APIEndPoint}notifications/archive/`,
      payload,
      headers
    )
    // Refresh the current list after archive
    await dispatch("fetchList", { params, headers, config })
    await dispatch("fetchStats", { headers, config })
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
